import type { UniverseSearchModel } from '../UniverseCreationModel';
import { getPresetsForSearchModel } from './conditionFromIndicator';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { ConcreteIndicator, IndicatorPreset } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { SelfAliasIndicatorFormula } from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { FormulaService } from 'src/services/FormulaService';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'PresetSelectionModel' });

export interface PresetOption {
    /** Normalized formula; used as the stable identity for view comparison. */
    readonly id: string;
    /** Display label rendered by `FormulaService.humanize`. */
    readonly humanized: string;
    /** Optional human-authored name on the preset. */
    readonly name: string | undefined;
    /** Raw match handed back through the change callback when this option is chosen. */
    readonly match: SelfAliasIndicatorFormula;
}

export interface PresetSelectionInit {
    indicator: ConcreteIndicator;
    /** Saved selection from wire (hydration). When omitted, the model defaults to the first option. */
    initialSelection?: SelfAliasIndicatorFormula;
    searchModel: UniverseSearchModel;
    formulaService: FormulaService;
}

/**
 * The current selection, modeled across its lifecycle:
 *
 *  - `none` — nothing selected: a new condition before options resolve, or a
 *    saved selection that resolved but matched no available preset.
 *  - `formula` — a raw wire formula not yet bound to a resolved option: the
 *    pre-resolution seed, plus the fallback when a saved formula can't be
 *    normalized. Keeps the wire round-trip exact without fabricating an option.
 *  - `option` — bound to a resolved `PresetOption`.
 */
export type PresetSelection =
    | { readonly kind: 'none' }
    | { readonly kind: 'formula'; readonly formula: SelfAliasIndicatorFormula }
    | { readonly kind: 'option'; readonly option: PresetOption };

interface PresetSelectionState {
    readonly options: readonly PresetOption[] | null;
    readonly selection: PresetSelection;
}

type PresetSelectionAction =
    | { type: 'SET_RESOLVED'; options: readonly PresetOption[]; selection: PresetSelection }
    | { type: 'SET_SELECTION'; selection: PresetSelection };

function reducer(state: PresetSelectionState, action: PresetSelectionAction): PresetSelectionState {
    switch (action.type) {
        case 'SET_RESOLVED':
            return { options: action.options, selection: action.selection };
        case 'SET_SELECTION':
            return { ...state, selection: action.selection };
        default:
            throw new UnreachableCaseError(action);
    }
}

type FormulaPreset = IndicatorPreset & { match: { type: 'formula' } };

interface PresetResolutionContext {
    formulaService: FormulaService;
    indicatorId: string;
    indicatorName: string;
}

async function resolvePresetOption(
    preset: FormulaPreset,
    context: PresetResolutionContext,
): Promise<PresetOption | null> {
    try {
        const [id, humanized] = await Promise.all([
            context.formulaService.normalize(preset.match.formula, preset.match.imports),
            context.formulaService.humanize(preset.match.formula),
        ]);
        return { id, humanized, name: preset.name, match: preset.match };
    } catch (error) {
        log.warn('Failed to resolve preset; skipping', {
            indicatorId: context.indicatorId,
            indicatorName: context.indicatorName,
            presetName: preset.name,
            formula: preset.match.formula,
            error,
        });
        return null;
    }
}

async function resolvePresetOptions(
    presets: readonly FormulaPreset[],
    context: PresetResolutionContext,
): Promise<readonly PresetOption[]> {
    const settled = await Promise.all(presets.map((preset) => resolvePresetOption(preset, context)));
    return settled.filter((option) => option != null);
}

async function resolveSelection(
    options: readonly PresetOption[],
    initialSelection: SelfAliasIndicatorFormula | undefined,
    context: PresetResolutionContext,
): Promise<PresetSelection> {
    if (initialSelection == null) {
        const first = options[0];
        return first != null ? { kind: 'option', option: first } : { kind: 'none' };
    }

    try {
        const normalized = await context.formulaService.normalize(initialSelection.formula, initialSelection.imports);
        const match = options.find((option) => option.id === normalized);
        // A saved selection that no longer matches any preset (e.g. presets drifted
        // since save) collapses to `none`.
        return match != null ? { kind: 'option', option: match } : { kind: 'none' };
    } catch (error) {
        log.warn('Failed to normalize saved selection; keeping raw formula', {
            indicatorId: context.indicatorId,
            formula: initialSelection.formula,
            error,
        });
        return { kind: 'formula', formula: initialSelection };
    }
}

export const PresetSelectionModel = createModel((init: PresetSelectionInit) => {
    const state = signal<PresetSelectionState>({
        options: null,
        // Seed with the raw wire formula so `selectedFormula` round-trips exactly
        // before the async build resolves it to an option (or leaves it unmatched).
        selection:
            init.initialSelection != null ? { kind: 'formula', formula: init.initialSelection } : { kind: 'none' },
    });

    const dispatch = (action: PresetSelectionAction) => {
        state.value = reducer(state.value, action);
    };

    effect(() => {
        const presets = getPresetsForSearchModel(init.indicator.presets, init.searchModel);
        let cancelled = false;
        const context: PresetResolutionContext = {
            formulaService: init.formulaService,
            indicatorId: init.indicator.id,
            indicatorName: init.indicator.name,
        };

        const resolve = async () => {
            const options = await resolvePresetOptions(presets, context);
            if (cancelled) return;
            const selection = await resolveSelection(options, init.initialSelection, context);
            if (cancelled) return;
            dispatch({ type: 'SET_RESOLVED', options, selection });
        };
        void resolve();

        return () => {
            cancelled = true;
        };
    });

    const options: ReadonlySignal<readonly PresetOption[] | null> = computed(() => state.value.options);
    const selectedOption: ReadonlySignal<PresetOption | null> = computed(() =>
        state.value.selection.kind === 'option' ? state.value.selection.option : null,
    );
    const selectedFormula: ReadonlySignal<SelfAliasIndicatorFormula | undefined> = computed(() => {
        const selection = state.value.selection;
        switch (selection.kind) {
            case 'none':
                return undefined;
            case 'formula':
                return selection.formula;
            case 'option':
                return selection.option.match;
            default:
                throw new UnreachableCaseError(selection);
        }
    });

    return {
        options,
        selectedOption,
        selectedFormula,
        setSelectedPreset(option: PresetOption | null) {
            dispatch({
                type: 'SET_SELECTION',
                selection: option != null ? { kind: 'option', option } : { kind: 'none' },
            });
        },
    };
});

export type PresetSelectionModelInstance = InstanceType<typeof PresetSelectionModel>;
